import { StationIndicators } from '@app/components/stations/StationIndicators';
import { useStationFavorites } from '@app/core/stations/hooks/useStationFavorites';
import { Station } from '@mi-front/core';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Box,
  ChipProps,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StationAvatar } from '../StationAvatar';
import { useSortedFilteredStations } from './useSelectorStations';

interface StationsNavigatorPopoverProps {
  readonly open: boolean;
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly stations: readonly Station[];
  readonly activeId: Station['id'];
  readonly onSelectStation: (station: Station) => void;
  readonly recentStationIds: readonly string[];
  readonly size?: ChipProps['size'];
}

export const StationsNavigatorPopover = (props: StationsNavigatorPopoverProps) => {
  const {
    open,
    anchorEl,
    onClose,
    stations,
    activeId,
    onSelectStation,
    recentStationIds,
    size = 'medium',
  } = props;
  const { $t } = useIntl();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const isSmall = size === 'small';
  const { favoriteIds: favoriteStationIds } = useStationFavorites();

  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  const { filteredStations, recentStations, sharedStations, favoriteStations, otherStations } =
    useSortedFilteredStations({
      stations,
      recentStationIds,
      favoriteStationIds,
      query,
    });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'Enter' && !!filteredStations.length) {
      onSelectStation(filteredStations[0]);
    }
  };

  const renderStationItem = (station: Station, index: number, isFirstInList: boolean) => {
    const showKeyboardIcon =
      isFirstInList && query.trim() !== '' && filteredStations[0]?.id === station.id;

    return (
      <ListItemButton
        key={station.id}
        onClick={() => onSelectStation(station)}
        selected={station.id === activeId}
        dense={isSmall}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListItemAvatar sx={{ minWidth: isSmall ? 24 : 32, mr: 1 }}>
            <StationAvatar stationId={station.id} size={isSmall ? 16 : 20} />
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box component="span">{station.name}</Box>
                <StationIndicators
                  station={station}
                  variant="icon"
                  size={isSmall ? 'small' : 'medium'}
                />
              </Box>
            }
            primaryTypographyProps={{
              variant: isSmall ? 'body2' : 'body1',
            }}
          />
        </Box>
        {showKeyboardIcon && (
          <Tooltip
            title={$t({
              id: 'ui.stationsSelector.enterToGo',
              defaultMessage: 'Enter para ir',
            })}
          >
            <KeyboardReturnIcon
              sx={{
                fontSize: isSmall ? 16 : 18,
                color: 'text.secondary',
                ml: 'auto',
              }}
            />
          </Tooltip>
        )}
      </ListItemButton>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableRestoreFocus
      TransitionProps={{
        onEntered: () => {
          inputRef.current?.focus();
        },
      }}
      PaperProps={{
        sx: {
          mt: 1,
          p: isSmall ? 1 : 1.5,
          width: isSmall ? 300 : 370,
          maxWidth: 'calc(100vw - 32px)',
        },
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={$t({
          id: 'ui.stationsSelector.searchPlaceholder',
          defaultMessage: 'Buscar emisora…',
        })}
        inputRef={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      <Box
        sx={{
          mt: 1,
          maxHeight: 'calc(50vh)',
          overflow: 'auto',
        }}
      >
        {!filteredStations.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: isSmall ? 0.75 : 1 }}>
            {$t({
              id: 'common.noResults',
              defaultMessage: 'Sin resultados',
            })}
          </Typography>
        ) : (
          <List disablePadding>
            {!!recentStations.length && (
              <>
                <ListSubheader sx={isSmall ? { lineHeight: 28, typography: 'caption' } : undefined}>
                  {$t({
                    id: 'stations.selector.recent',
                    defaultMessage: 'Recientes',
                  })}
                </ListSubheader>
                {recentStations.map((station, index) =>
                  renderStationItem(station, index, index === 0),
                )}
              </>
            )}
            {!!favoriteStations.length && (
              <>
                <ListSubheader
                  sx={{
                    ...(isSmall ? { lineHeight: 28, typography: 'caption' } : {}),
                    ...(recentStations.length ? { mt: isSmall ? 1 : 1.5 } : {}),
                  }}
                >
                  {$t({
                    id: 'stations.selector.favorites',
                    defaultMessage: 'Favoritas',
                  })}
                </ListSubheader>
                {favoriteStations.map((station, index) =>
                  renderStationItem(station, index, !recentStations.length && index === 0),
                )}
              </>
            )}
            {!!otherStations.length && (
              <>
                {(!!recentStations.length || !!favoriteStations.length) && (
                  <ListSubheader
                    sx={{
                      ...(isSmall ? { lineHeight: 28, typography: 'caption' } : {}),
                      mt: isSmall ? 1 : 1.5,
                    }}
                  >
                    {$t({
                      id: 'stations.selector.allStations',
                      defaultMessage: 'Todas las emisoras',
                    })}
                  </ListSubheader>
                )}
                {otherStations.map((station, index) =>
                  renderStationItem(
                    station,
                    index,
                    !recentStations.length && !favoriteStations.length && index === 0,
                  ),
                )}
              </>
            )}
            {!!sharedStations.length && (
              <>
                <ListSubheader
                  sx={{
                    ...(isSmall ? { lineHeight: 28, typography: 'caption' } : {}),
                    ...(recentStations.length || favoriteStations.length || otherStations.length
                      ? { mt: isSmall ? 1 : 1.5 }
                      : {}),
                  }}
                >
                  {$t({
                    id: 'stations.selector.shared',
                    defaultMessage: 'Emisoras compartidas',
                  })}
                </ListSubheader>
                {sharedStations.map((station, index) =>
                  renderStationItem(
                    station,
                    index,
                    !recentStations.length &&
                      !favoriteStations.length &&
                      !otherStations.length &&
                      index === 0,
                  ),
                )}
              </>
            )}
          </List>
        )}
      </Box>
    </Popover>
  );
};
